import { css } from "@emotion/css";
import type { PageTitleBadge } from "@octopusdeploy/design-system-components";
import { Button, CheckboxNew, ExternalLink } from "@octopusdeploy/design-system-components";
import { SettingsIdValues, Permission } from "@octopusdeploy/octopus-server-client";
import type { MetadataTypeCollection, SettingsMetadataItemResource, SensitiveValue, Repository } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import { OctoLink } from "@octopusdeploy/utilities";
import pluralize from "pluralize";
import { useConnectivityTestUrl, validateIntegrationSettings } from "~/areas/configuration/components/Settings/shared/integrationSettings";
import { repository } from "~/clientInstance";
import { isLicensedFeatureAvailable } from "~/components/LicensedFeature/LicensedFeatureContext";
import { PageContent } from "~/components/PageContent/PageContent";
import { RemoveItemsList } from "~/components/RemoveItemsList/RemoveItemsList";
import Section from "~/components/Section";
import { RequestFeatureTrialUpsellDialog } from "~/components/UpsellDialog/RequestFeatureTrialUpsellDialog";
import { UpsellJiraServiceManagementIntegrationIllustration } from "~/components/UpsellDialog/UpsellJiraServiceManagementIntegrationIllustration";
import { ExpandableFormSection, Summary } from "~/components/form";
import { Form, useForm } from "~/components/form/Form/Form";
import { useWorkflow } from "~/workflows/useWorkflow";
import { workflows } from "~/workflows/workflows";
export interface JiraServiceManagementIntegrationConnection {
    Id: string | undefined;
    ConnectionName: string | undefined;
    BaseUrl: string | undefined;
    Username: string | undefined;
    Token: SensitiveValue;
}
export interface JiraServiceManagementIntegrationSettings {
    Id: string;
    IsEnabled: boolean;
    WorkNotesIsEnabled: boolean;
    Connections: JiraServiceManagementIntegrationConnection[];
}
class ConnectionRemoveItemsList extends RemoveItemsList<JiraServiceManagementIntegrationConnection> {
}
interface JiraServiceManagementIntegrationSettingsPageLoaderData {
    settingsMetadata: SettingsMetadataItemResource;
    types: MetadataTypeCollection;
    values: JiraServiceManagementIntegrationSettings;
    isJiraSMIFeatureAvailable: boolean;
    isTrial: boolean;
}
interface JiraServiceManagementIntegrationSettingsPageProp {
    loaderData: JiraServiceManagementIntegrationSettingsPageLoaderData;
}
export async function jiraServiceManagementIntegrationSettingsPageLoader(repository: Repository) {
    const settingsResponse = await repository.Settings.getById(SettingsIdValues.JiraServiceManagementIntegration);
    const metadata = repository.Settings.getMetadata(settingsResponse);
    const values = repository.Settings.getValues(settingsResponse);
    const isJiraSMIFeatureAvailable = isLicensedFeatureAvailable("JiraServiceManagementIntegration");
    const licenseStatusAsync = repository.Licenses.getCurrentStatus();
    return {
        settingsMetadata: settingsResponse,
        types: await metadata,
        values: await values,
        isJiraSMIFeatureAvailable: await isJiraSMIFeatureAvailable,
        isTrial: (await licenseStatusAsync).IsTrial,
    };
}
export function JiraServiceManagementIntegrationSettingsPage({ loaderData }: JiraServiceManagementIntegrationSettingsPageProp) {
    const { model, setModel, formProps, createSaveAction } = useForm({
        formName: "Jira Service Management Integration",
        initialModel: loaderData.values,
        onSubmit: async (repository, settings) => {
            validateIntegrationSettings(loaderData.types, settings);
            return repository.Settings.saveValues(loaderData.settingsMetadata, settings);
        },
        updateFormModelAfterSubmit: (model) => model,
        snackbarSaveText: "Jira Service Management Integration updated",
        submitPermission: { permission: [Permission.ConfigureServer] },
    });
    const startAddJiraConnectionWorkflow = useWorkflow(workflows.addJiraServiceManagementConnectionWorkflow);
    const startEditJiraConnectionWorkflow = useWorkflow(workflows.editJiraServiceManagementConnectionWorkflow);
    const connectivityTestUrl = useConnectivityTestUrl(loaderData.types, "JiraServiceManagementIntegrationConnectionResource", "Token");
    const addConnection = (connection: JiraServiceManagementIntegrationConnection) => {
        setModel((prev) => ({
            ...prev,
            Connections: [...prev.Connections, connection],
        }));
    };
    const removeConnection = (connection: JiraServiceManagementIntegrationConnection) => {
        setModel((prev) => ({
            ...prev,
            Connections: prev.Connections.filter((existingConnection) => existingConnection !== connection),
        }));
    };
    const editConnection = (connection: JiraServiceManagementIntegrationConnection) => {
        setModel((prev) => ({
            ...prev,
            Connections: prev.Connections.map((existingConnection) => (existingConnection.Id === connection.Id ? connection : existingConnection)),
        }));
    };
    const tooltipContent = loaderData.isJiraSMIFeatureAvailable ? undefined : "Request a trial to try the Jira Service Management Integration.";
    const badgeConfig: PageTitleBadge | undefined = loaderData.isTrial ? { variant: "upsell", label: "Enterprise" } : undefined;
    return (<PageContent header={{ primaryAction: { ...createSaveAction(), extraContext: tooltipContent }, title: "Jira Service Management Integration", badge: badgeConfig, breadcrumbs: [{ label: "Settings", pageUrl: links.settingsPage.generateUrl() }] }}>
            <Section className={styles.settingOverviewSection}>
                {loaderData.isJiraSMIFeatureAvailable ? (<p className={styles.settingOverviewText}>
                        Integrate Jira Service Management Change Requests into your deployments. <ExternalLink href="JiraServiceManagementIntegration">Learn more</ExternalLink>.
                    </p>) : (<RequestFeatureTrialUpsellDialog triggerVariant="inline-message" triggerContent="The Jira Service Management integration is available on the Enterprise tier." upsellDialogTitle="Enforce change approvals automatically with the Jira Service Management (JSM) integration" docsLabel="Docs: Jira Service Management Integration" docsLink={OctoLink.Create("JiraServiceManagementIntegration")} requestTrialDialogTitle="Request a free trial of the Jira Service Management integration" featureName="Jira Service Management Integration" onRequestTrial={async () => {
                await repository.Settings.requestJiraServiceManagementIntegrationTrial();
            }} upsellDialogSidePanelImage={<UpsellJiraServiceManagementIntegrationIllustration />}>
                        <p>The JSM integration blocks deployments or runbooks until there's an approved change request. It links to existing requests or automatically creates new ones, so you stay compliant without the manual work.</p>
                        <p>Available on the Enterprise tier.</p>
                    </RequestFeatureTrialUpsellDialog>)}
            </Section>
            <Form {...formProps} expandAllOnMount>
                <ExpandableFormSection key="IsEnabled" errorKey="IsEnabled" title="Enabled" summary={model.IsEnabled ? Summary.summary("Yes") : Summary.summary("No")} help="If enabled, you can turn on change control on your projects and environments so that certain deployments require an approved Jira Service Management Change Request." expandable>
                    <CheckboxNew value={!!model.IsEnabled} disabled={!loaderData.isJiraSMIFeatureAvailable} onChange={(IsEnabled) => setModel((prev) => ({ ...prev, IsEnabled }))} label="Enabled"/>
                </ExpandableFormSection>
                <ExpandableFormSection key="Connections" errorKey="Connections" title="Connections" summary={model.Connections?.length ? Summary.summary(`${model.Connections?.length} ${pluralize("connection", model.Connections?.length)}`) : Summary.summary("No connections")} help="Connect your Octopus instance to one or more Jira Service Management instances." expandable>
                    <div>
                        <ConnectionRemoveItemsList empty={""} onRow={(data, index) => <div key={index}>{data.ConnectionName}</div>} data={model.Connections || []} listActions={[<Button label="Add Connection" importance="secondary" disabled={!loaderData.isJiraSMIFeatureAvailable} onClick={() => startAddJiraConnectionWorkflow({ connectivityTestUrl }, addConnection)}/>]} onRemoveRow={loaderData.isJiraSMIFeatureAvailable ? (connection) => removeConnection(connection) : undefined} onRowTouch={(connection) => startEditJiraConnectionWorkflow({ connectivityTestUrl, connection, isDisabled: !loaderData.isJiraSMIFeatureAvailable }, editConnection)}/>
                    </div>
                </ExpandableFormSection>
                <ExpandableFormSection key="WorkNotesIsEnabled" errorKey="WorkNotesIsEnabled" title="Customer Comments Enabled" summary={model.WorkNotesIsEnabled ? Summary.summary("Yes") : Summary.summary("No")} help="If enabled, customer visible comments will be added to the linked issue at start/end of deployment." expandable>
                    <CheckboxNew value={!!model.WorkNotesIsEnabled} disabled={!loaderData.isJiraSMIFeatureAvailable} onChange={(WorkNotesIsEnabled) => setModel((prev) => ({ ...prev, WorkNotesIsEnabled }))} label="Customer Comments Enabled"/>
                </ExpandableFormSection>
            </Form>
        </PageContent>);
}
const styles = {
    settingOverviewSection: css({
        margin: "0 !important",
    }),
    settingOverviewText: css({
        margin: 0,
    }),
};
